// Installing the app to the home screen.
//
// Worth doing on the floor: an installed PWA opens without browser chrome, so
// the address bar cannot be tapped and the app fills a Zebra's screen properly.
//
// Two paths, because the platforms genuinely differ:
//
//  · Chrome (which is what the Zebras run) fires `beforeinstallprompt`. That
//    event is caught at module scope in lib/installPrompt — long before this
//    hook mounts — and replayed when the operator asks for it.
//  · iOS Safari has no API at all. Nothing can be triggered, so the only
//    honest thing is to describe the Share-sheet route and let the user do it.

import { useCallback, useSyncExternalStore } from 'react';
import { installStore } from '@/lib/installPrompt';

export type InstallMethod = 'prompt' | 'ios-manual' | 'none';

export interface InstallPrompt {
  /** How (or whether) this device can install. */
  method: InstallMethod;
  /** Already running as an installed app — nothing to offer. */
  installed: boolean;
  /** Chrome's prompt. Resolves to whether the operator accepted. */
  install: () => Promise<boolean>;
}

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua);
  // Chrome and Firefox on iOS cannot install either, and say so in the UA.
  const safari = !/CriOS|FxiOS|EdgiOS/.test(ua);
  return ios && safari;
}

export function useInstallPrompt(): InstallPrompt {
  // Subscribing to the store rather than holding the event in component state
  // means a banner mounted at any point — including long after the event
  // arrived — still sees it.
  const deferred = useSyncExternalStore(
    installStore.subscribe,
    installStore.get,
    () => null,
  );
  const installed = useSyncExternalStore(
    installStore.subscribe,
    installStore.installed,
    () => false,
  );

  const install = useCallback(async () => {
    const event = installStore.get();
    if (!event) return false;
    await event.prompt();
    const { outcome } = await event.userChoice;
    // Chrome will not replay the same event, so drop it either way rather than
    // leaving a button that silently does nothing the second time.
    installStore.clear();
    return outcome === 'accepted';
  }, []);

  const method: InstallMethod = installed
    ? 'none'
    : deferred
      ? 'prompt'
      : isIosSafari()
        ? 'ios-manual'
        : 'none';

  return { method, installed, install };
}
